import { Component, OnInit, Input, OnDestroy, HostListener, AfterViewChecked } from '@angular/core';
import * as moment from 'moment';
import { MessageModel } from 'src/app/core/models/chat/message.model';
import { Subject } from 'rxjs';
import { EntityChanel } from 'src/app/core/models/chat/entity-chanel.model';
import { ChatDisplayStatus } from 'src/app/core/models/chat/chat-display.status';
import { ChannelFilter } from 'src/app/core/models/chat/channel.filter';
import { AppState } from 'src/app/app.reducer';
import { Store, select } from '@ngrx/store';
import * as chatActions from '../../actions/chat.actions';
import * as chatSelectors from '../../selectors/chat.selector';
import { ChatConversation } from 'src/app/core/models/chat/chat-conversation.model';
import { BuyerVendorModel } from 'src/app/core/models/chat/buyer-vendor.model';
import { currentSite, currentSiteID, currentCompanyType } from '../../selectors/auth.selector';
import { ChatEntityType } from 'src/app/core/enums/chat-entity-type.enum';
import { PubnubService } from 'src/app/core/services/chat/pubnub.service';
import { COMPANY_TYPE } from 'src/app/core/enums/company-type.enum';
import { debounceTime, take, takeUntil, } from 'rxjs/operators';

@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Input() chatDisplayStatus: ChatDisplayStatus;

    ngUnsubscribe: Subject<void> = new Subject<void>();
    pubnub: any;
    isLoading: boolean;
    channel: EntityChanel = new EntityChanel();
    chatConversation: ChatConversation = new ChatConversation();
    buyersVendors: Array<BuyerVendorModel>;
    message: string;
    site: any;
    currentSiteId: number;
    test: any;
    isFromBuyerSide: boolean;
    keyword = '';
    public search$ = new Subject<any>();
    scrollContainer: any;
    needsScroll = true;
    destroySubscription$: Subject<void> = new Subject<void>();

    constructor(private store: Store<AppState>, private pubnubService: PubnubService) { }

    ngOnInit() {
        this.store.pipe(select(currentSiteID), takeUntil(this.destroySubscription$)).subscribe(siteId => {
            this.currentSiteId = siteId;

            this.store.pipe(select(currentSite, { currentSiteId: this.currentSiteId }),
                takeUntil(this.destroySubscription$)).subscribe(site => {
                    this.site = site;
                });
        });

        this.store.select(currentCompanyType).pipe(takeUntil(this.destroySubscription$)).subscribe(companyType => {
            this.isFromBuyerSide = companyType === COMPANY_TYPE.BUYER;
        });

        this.store.pipe(select(chatSelectors.getChatDisplayStatus),
            takeUntil(this.destroySubscription$)).subscribe((data: ChatDisplayStatus) => {
                if (data && data.showChat) {
                    this.chatDisplayStatus = data;

                    if (!this.isGeneralEntityType()) {
                        this.createOrderChatConversation();
                    }

                    if (this.chatDisplayStatus.buyerVendor && this.chatDisplayStatus.showChat) {
                        this.createGeneralChatChatConversation(this.chatDisplayStatus.buyerVendor);
                    }
                }
            });

        this.search$.pipe(debounceTime(500), takeUntil(this.destroySubscription$)).subscribe(() => {
            this.store.dispatch(new chatActions.LoadBuyerVendor(this.keyword));
        });
    }

    ngAfterViewChecked() {
        const element = document.querySelector('#bottom');
        if (this.needsScroll && element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });
            this.needsScroll = false;
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    sendMessage() {
        if (this.message) {
            const messageData = {
                member: {
                    displayName: this.site.displayName,
                    secretKey: this.site.secretKey,
                    acronym: this.site.acronym,
                },
                message: {
                    acronym: this.site.acronym,
                    text: this.message,
                    description: 'test description',
                    typeId: 1,
                    fromBuyerSide: this.isFromBuyerSide,
                    channelUuid: this.chatConversation.channel.uuid || this.getChannelUUID(),
                },
                channel: {
                    vendorSiteKey: this.chatConversation.channel.vendorSiteKey,
                    buyerSiteKey: this.chatConversation.channel.buyerSiteKey,
                    entity: '',
                    entityTypeId: this.chatConversation.channel.entityTypeId,
                    channelUuid: this.chatConversation.channel.uuid || this.getChannelUUID(),
                    entityId: this.chatConversation.channel.entityId
                },
                action: 1
            };

            this.pubnub.publish({
                channel: this.getChannelUUID(),
                message: messageData
            });
        }
    }

    initPubnub() {
        this.pubnub = this.pubnubService.getPubNub();
        // remove listeners and subscribers previously added to this chat/channel
        this.pubnub.removeAllListeners();
        this.pubnub.unsubscribeAll();

        this.pubnub.addListener({
            message: (event) => {
                this.updateMessageList(event);
            },
            presence(event) {
                this.updateMessageList(event);
            }
        });
        this.pubnub.subscribe({
            channels: [this.getChannelUUID()],
            withPresence: true
        });
    }

    getChannelUUID() {
        return this.channel.uuid ? this.channel.uuid : 'Order.' + this.chatDisplayStatus.entity;
    }

    isGeneralEntityType(): boolean {
        return this.chatDisplayStatus.entityTypeId === ChatEntityType.General;
    }

    onCloseChat() {
        this.resetChatStatus();
        this.store.dispatch(new chatActions.ChatDisplayStatusAction(this.chatDisplayStatus));
    }

    resetChatStatus(): void {
        this.chatDisplayStatus.showChat = false;
        this.chatDisplayStatus.showOnlyInbox = false;
        this.chatDisplayStatus.showNewConversation = false;
        this.chatDisplayStatus.entityTypeId = null;
        this.chatDisplayStatus.entity = null;
        this.chatDisplayStatus.entityId = null;
        this.chatDisplayStatus.buyerVendor = null;
        this.chatDisplayStatus.chatClosed = true;
    }

    getDate(date): string {
        return moment.utc(date).local().format('L,  LT');
    }

    openConversationChat() {
        this.chatDisplayStatus.showNewConversation = false;
    }

    getChatTitle(): string {
        return this.chatDisplayStatus.showNewConversation ? 'New Conversation' : this.channel.entity;
    }

    openNewConversation() {
        this.store.pipe(select(chatSelectors.getBuyersVendors), takeUntil(this.destroySubscription$)).subscribe(data => {
            this.buyersVendors = data;
        });
        this.store.dispatch(new chatActions.LoadBuyerVendor(this.keyword));
        this.chatDisplayStatus.showOnlyInbox = false;
        this.chatDisplayStatus.showNewConversation = true;
        this.chatDisplayStatus.showChat = false;
    }

    openConversationWithCustomer(channel: EntityChanel) {
        this.channel = channel;
        if (this.channel.id > 0) {
            this.store.select(chatSelectors.getConversationsByChannel, { channelId: this.channel.id })
                .pipe(takeUntil(this.destroySubscription$)).subscribe(data => {
                    this.chatConversation = data;
                });
        }

        this.store.dispatch(new chatActions.LoadConversation(this.initFilter(this.channel)));
        this.chatDisplayStatus.showNewConversation = false;
        this.chatDisplayStatus.showOnlyInbox = false;
        this.chatDisplayStatus.showChat = true;

        if (this.isGeneralEntityType()) {
            this.initPubnub();
        }
    }

    createGeneralChatChatConversation(buyerVendor: BuyerVendorModel) {
        this.store.select(chatSelectors.getConversationsByChannel, { channelId: 0 })
            .pipe(takeUntil(this.destroySubscription$)).subscribe(data => {
                if (data) {
                    this.chatConversation = data;
                    this.chatConversation.channel.entity = buyerVendor.name;
                    this.chatConversation.channel.buyerSiteKey = this.site.secretKey;
                    this.chatConversation.channel.vendorSiteKey = buyerVendor.secretKey;
                    this.chatConversation.channel.entityTypeId = this.chatDisplayStatus.entityTypeId;
                    this.chatConversation.channel.entityId = -1;
                    this.openConversationWithCustomer(this.chatConversation.channel);
                }
            });
        this.store.dispatch(new chatActions.GenerateChannelUUID(this.chatDisplayStatus.entityTypeId));
    }

    createOrderChatConversation() {
        this.chatConversation = new ChatConversation();
        const channel = new EntityChanel();
        channel.entityTypeId = this.chatDisplayStatus.entityTypeId;
        channel.entityId = this.chatDisplayStatus.entityId;
        channel.entity = this.chatDisplayStatus.entity;

        this.chatConversation.channel = channel;
        this.store.select(chatSelectors.getEntityChannel, { entityId: this.chatDisplayStatus.entityId })
            .pipe(takeUntil(this.destroySubscription$)).subscribe(storeChannel => {
                if (storeChannel) {
                    this.channel.uuid = storeChannel.uuid;

                    this.store.select(chatSelectors.getConversationsByChannel, { channelId: storeChannel.id })
                        .pipe(takeUntil(this.destroySubscription$)).subscribe(data => {
                            if (data) {
                                this.chatConversation = data;
                                this.chatConversation.channel.entity = this.chatDisplayStatus.entity;
                                this.chatConversation.channel.buyerSiteKey = this.site.secretKey;

                                this.initPubnub();
                            }
                        });
                } else {
                    this.initPubnub();
                }
            });

        this.openConversationWithCustomer(this.chatConversation.channel);
    }

    closeInbox() {
        this.chatDisplayStatus.showOnlyInbox = false;
        this.chatDisplayStatus.showChat = true;
    }

    initFilter(entityChanel: EntityChanel): ChannelFilter {
        return {
            currentPage: 1,
            entityId: entityChanel.entityId,
            entityTypeId: entityChanel.entityTypeId,
            pageSize: 50
        };
    }

    updateMessageList(event: any) {
        const message = new MessageModel();

        message.acronym = event.message.member.acronym;
        message.memberKey = event.message.member.secretKey;
        message.channelUuid = event.message.message.channelUuid;
        message.text = event.message.message.text;
        message.fromBuyerSide = event.message.message.fromBuyerSide;
        message.createdDate = new Date(this.getDate(event.timetoken / 1e4)).toISOString();
        message.typeId = event.message.message.typeId;
        message.displayName = event.message.member.displayName;

        this.chatConversation.messages.push(message);
        this.chatConversation.channel.latestMessageDate = message.createdDate;
        this.chatConversation.channel.latestmessage = message.text;
        this.needsScroll = true;
        this.message = null;
    }

    searchBuyerVendor() {
        this.store.pipe(debounceTime(1500));
    }

    @HostListener('window:keyup', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (event.keyCode === 13 && this.message !== null) {
            this.sendMessage();
        }
    }

    goToInbox() {
        this.keyword = '';
        this.chatDisplayStatus.showOnlyInbox = !this.chatDisplayStatus.showOnlyInbox;
        this.chatDisplayStatus.showChat = false;
        this.chatDisplayStatus.showNewConversation = true;
    }
}
